import React, { useState, useEffect } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { usePrevious } from 'Hooks/CustomHooks';
import { Phase } from 'Common/Controls/Dms/MultiMsg/Phase';
import { OpStatus, Graphic } from 'NSwag/ServerClient/Client.nswag';
import { getDmsDisplayColor } from 'Common/Controls/Dms/MultiMsg/MultiUtils';
import { PhaseAlignment } from 'DataLibrary/Dms/MultiMsgConstants';
import MultiMsgLineDisplay, { FocusPosition } from 'Common/Controls/Dms/MultiMsg/MultiMsgDisplay/MultiMsgLineDisplay';
import DmsClient from 'Clients/DmsClient';
import { getDynamicStyles } from 'jss';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
    },
    justifyContainer: {
    },
    image: {
        width: '100%',
        height: '100%',
        objectFit: 'contain'
    },
    phaseContainer: {
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        borderRadius: '5px',
        margin: '15px',
        border: '9px solid',
        backgroundColor: '#222',
        color: 'grey',
        padding: theme.spacing(1, 2),
        '.MuiCardContent-root &': {
            margin: '0px',
            border: '0px',
        }
    },
    textContainer: {
        display: 'flex',
        flex: '1 1 auto',
        maxWidth: '100%',
        flexDirection: 'column' as 'column'
    },
    addDeleteContainer: {
    }
}));

type alignmentStyles = 'flex-start' | 'center' | 'flex-end'

interface IDynamicStyles {
    color: string,
    justifyContent: alignmentStyles,
    width: string,
    height: string
}

const getStyles = (phase: Phase, opStatus: OpStatus) => {
    let style = {
        color: getDmsDisplayColor(opStatus),
        width: `calc(${phase.colCount / 2}em + 12px)`,
        height: `calc(${phase.lineCount}em + 24px)`
    }

    return style;
}

const getImageStyles = (phase: Phase, graphic: Graphic) => {
    let style = {
        width: `${(graphic.width! / phase.signDimensions.width) * 100}%`,
        height: `${(graphic.height! / phase.signDimensions.height) * 100}%`
    }

    return style;
}

const getTextStyles = (phase: Phase) => {
    let style = {
        justifyContent: 'center' as alignmentStyles
    }

    switch (phase.alignment) {
        case PhaseAlignment.Top:
            style.justifyContent = 'flex-start' as alignmentStyles
            break;
        case PhaseAlignment.Bottom:
            style.justifyContent = 'flex-end' as alignmentStyles
            break;
    }

    return style;
}

interface IProps {
    value: string[];
    phase: Phase;
    opStatus: OpStatus,
    onFocus?: () => void;
    onShiftFocus: (targetIndex: number) => void;
    focused: FocusPosition | null;
    editable?: boolean;
}

const MultiMsgPhaseDisplay: React.FC<IProps> = ({ value, phase, opStatus, onFocus, onShiftFocus, focused, editable }: IProps) => {

    const classes = useStyles();

    const [focusedLine, setFocusedLine] = useState<number>(-1);
    const [graphic, setGraphic] = useState<Graphic | null>(null);
    const prevFocusedLine = usePrevious(focusedLine);

    const shiftFocus = (targetIndex: number) => {
        if (targetIndex < 0 || targetIndex >= phase.lines.length) {
            let targetPhaseIndex = targetIndex === -1 ? phase.index - 1 : phase.index + 1;
            onShiftFocus(targetPhaseIndex);
        } else {
            setFocusedLine(targetIndex);
            if (focused == null && onFocus) {
                onFocus();
            }
        }
    }

    useEffect(() => {
        if (focused != null) {
            let lineIndex = focused === FocusPosition.End ? phase.lines.length - 1 : 0;
            setFocusedLine(lineIndex);
        }
    }, [focused]);

    useEffect(() => {
        if (phase.graphicId != -1) {
            DmsClient.getGraphic(phase.graphicId).then(resp => {
                if (resp && resp.data) {
                    let graphic = resp.data[Object.keys(resp.data)[0]];
                    if (graphic && graphic.graphicData) {
                        setGraphic(graphic);
                    } else {
                        setGraphic(null);
                    }
                } else {
                    setGraphic(null);
                }
            }).catch(error => {
                setGraphic(null);
            });
        } else {
            setGraphic(null);
        }
    }, [phase.graphicId]);

    const focusPosition = prevFocusedLine == null || prevFocusedLine > focusedLine
        ? FocusPosition.End
        : FocusPosition.Start;

    return (
        <div className={classes.phaseContainer} style={getStyles(phase, opStatus)}>
            {
                phase && graphic && graphic.graphicData &&
                <img className={classes.image} style={getImageStyles(phase, graphic)} src={`data:image/jpeg;base64, ${graphic.graphicData}`} />
            }
            <div className={classes.textContainer} style={getTextStyles(phase)}>
            {
                phase && value.map((line, index) =>
                    <MultiMsgLineDisplay
                        key={index}
                        text={line}
                        onFocus={() => {
                            setFocusedLine(index)
                            if (onFocus) onFocus()
                        }}
                        onBlur={() => {
                            setFocusedLine(-1)
                        }}
                        onShiftFocus={shiftFocus}
                        focused={focusedLine === index ? focusPosition : null}
                        line={phase.lines[index]}
                        editable={editable}
                    />
                )
                }
            </div>
        </div>
    );
}

export default MultiMsgPhaseDisplay;

export type FocusProps = {
    index: number,
    position: FocusPosition
}